<template>
  <!-- The fallback portrait shows immediately (ready=false) and stays until the
       recolored sprite canvas is ready — so the swap never flashes empty and
       never regresses if a class has no walk sprite.
       Two canvases: the in-flow WALK canvas (idle loop) and an absolutely-
       positioned ARMED overlay that only appears during an attack burst (so the
       weapon can sweep outside the portrait box without shifting layout). The
       overlay only exists during a burst: just the live match's sprites ever
       attack, and a page holds ~100 sprites. -->
  <span class="unit-sprite" :class="{ flipped: flip }" :style="{ height: size + 'px' }">
    <canvas v-show="ready && !attacking" ref="cv" class="us-cv"></canvas>
    <canvas v-if="attacking" ref="atk" class="us-atk"></canvas>
    <img v-if="!ready" class="us-fb" :src="fallbackSrc" :alt="alt"
         @error="$event.target.style.visibility = 'hidden'">
  </span>
</template>

<script>
// A walking-sprite portrait for a single unit. Resolves the unit's atlas
// key+palette (spriteForClass — tiny), then lazily pulls the heavy sprite
// renderer (manifest.json + canvas LUT) so this component is cheap to import
// into the entry bundle. See tools/fft-sprite/HANDOFF.md ("replacing the
// headshots").
//
// With `attackBursts` on (the live-match bracket cell), each sprite also
// occasionally plays a ONE-SHOT attack animation — the armed swing/poke/bow/gun
// pose with the unit's real Mainhand composited over it (lib/spriteArmed, the
// same path GalleryView uses), falling back to the unarmed `attack` pose for
// monsters, bare hands, or weapons we don't have ripped art for.
import { spriteForUnit, hasWalkSprite } from '../lib/spriteForClass.js';

// key:pal:anim -> Promise<{front?,back?}|null>. The recolored canvases are shared
// across every cell showing the same class+color+anim (HANDOFF: "cache by key:pal").
const spriteCache = new Map();
let _render = null;       // the heavy spriteRender module, imported once
let _armedLib = null;     // the armed-weapon overlay lib, imported once
async function renderMod() { if (!_render) _render = await import('../lib/spriteRender.js'); return _render; }
async function armedMod() { if (!_armedLib) _armedLib = await import('../lib/spriteArmed.js'); return _armedLib; }
async function loadCached(key, pal, anim) {
  const k = key + ':' + pal + ':' + anim;
  let p = spriteCache.get(k);
  if (!p) { p = renderMod().then((m) => m.loadSprite(key, pal, anim)); spriteCache.set(k, p); }
  return p;
}

// ONE requestAnimationFrame for every animated UnitSprite on the page — a
// per-component rAF for dozens of canvases melts a phone (HANDOFF watch-out).
const live = new Set();
let raf = 0;
function frame(now) {
  for (const inst of live) inst._draw(now);
  raf = live.size ? requestAnimationFrame(frame) : 0;
}
function startTicking(inst) { live.add(inst); if (!raf) raf = requestAnimationFrame(frame); }
function stopTicking(inst) { live.delete(inst); if (!live.size && raf) { cancelAnimationFrame(raf); raf = 0; } }

// Padding (sprite px) around the armed body so a raised weapon (a lifted blade
// reaches ~47px from the origin) doesn't clip; the body sits inset at (X, T).
const PAD = { X: 24, T: 34, B: 6 };
// Per-unit gap between attack bursts (ms). Randomized in this range and started
// from a random offset per instance, so the ~8 cell units never swing in unison.
const BURST_MIN = 6000, BURST_MAX = 15000;

function prefersReducedMotion() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default {
  name: 'UnitSprite',
  props: {
    // The unit (needs Class + Gender; Mainhand drives the armed weapon). Resolved via spriteForUnit.
    unit: { type: Object, required: true },
    // Team color NAME ('Red'/'Champion'/…); ignored for monsters (family palette).
    teamColor: { type: String, default: '' },
    // Portrait shown while loading / when the class has no walk sprite.
    fallbackSrc: { type: String, default: '' },
    // Animate on the shared ticker, or draw a static first frame. Default static
    // for perf when many are on screen; the active-fight cell passes true.
    animated: { type: Boolean, default: false },
    // Occasionally interrupt the walk loop with a one-shot armed attack. Only the
    // live-match bracket cell opts in; needs `animated` too. Honors reduced-motion.
    attackBursts: { type: Boolean, default: false },
    // Mirror horizontally (left team faces right, as the headshots did).
    flip: { type: Boolean, default: false },
    // Target display height in px.
    size: { type: Number, default: 48 },
  },
  data() { return { ready: false, attacking: false }; },
  computed: {
    alt() { return (this.unit && this.unit.Class) || ''; },
  },
  watch: {
    'unit.Class': 'reload',
    'unit.Gender': 'reload',
    'unit.Mainhand': 'resetArmed',
    teamColor: 'reload',
    animated(v) { if (v) this.maybeStart(); else { stopTicking(this); this.stopBursts(); this.renderStatic(); } },
    attackBursts(v) { if (v) this.scheduleBurst(); else this.stopBursts(); },
  },
  mounted() { this.reload(); },
  beforeUnmount() { this._dead = true; stopTicking(this); this.clearBurstTimer(); },
  methods: {
    async reload() {
      stopTicking(this);
      this.stopBursts();
      this.ready = false;
      this._pose = null;
      this._poseName = null;
      this.resetArmed();
      const u = this.unit;
      // Unknown class (e.g. a unique raid boss with no walk sprite) → keep the
      // real headshot rather than rendering a wrong stand-in.
      if (!u || !hasWalkSprite(u.Class, u.Gender)) return;
      const { key, pal } = spriteForUnit(u.Class, u.Gender, this.teamColor);
      this._key = key; this._pal = pal;
      const token = (this._token = (this._token || 0) + 1);
      const sprite = await loadCached(key, pal, 'walk');
      if (token !== this._token || this._dead) return;   // unit/color changed mid-load
      const poseName = sprite && (sprite.front ? 'front' : (sprite.back ? 'back' : null));
      if (!poseName) return;                             // stay on the fallback img
      this._pose = sprite[poseName];
      this._poseName = poseName;
      this._t0 = 0;
      this.ready = true;
      this.$nextTick(() => {
        const cv = this.$refs.cv;
        if (!cv) return;
        cv.width = this._pose.fw; cv.height = this._pose.fh;
        this.renderStatic();
        this.maybeStart();
      });
    },
    maybeStart() {
      if (this.animated && this._pose) startTicking(this);
      this.scheduleBurst();
    },
    renderStatic() { if (this._pose) this.blit(0); },   // frame 0 when static / pre-tick

    // ---- walk loop (in-flow canvas) ----
    blit(f) {
      const cv = this.$refs.cv, p = this._pose;
      if (!cv || !p) return;
      const cx = cv.getContext('2d');
      cx.imageSmoothingEnabled = false;
      cx.clearRect(0, 0, p.fw, p.fh);
      cx.drawImage(p.canvas, f * p.fw, 0, p.fw, p.fh, 0, 0, p.fw, p.fh);
    },
    _draw(now) {
      // Playing a one-shot attack? Render the armed overlay until the cycle ends.
      if (this.attacking && this._armed) {
        const p = this._armed.pose;
        const delays = (p.delaysCs && p.delaysCs.length) ? p.delaysCs : new Array(p.frames).fill(8);
        let total = 0; const cum = delays.map((d) => (total += d * 10));   // cs → ms
        if (!this._atk) this._atk = now;
        const t = now - this._atk;
        if (t >= total) { this.endBurst(); return; }
        let f = 0; while (f < cum.length - 1 && t >= cum[f]) f++;
        this.drawArmed(f);
        return;
      }
      const p = this._pose; if (!p) return;
      const delays = (p.delaysCs && p.delaysCs.length) ? p.delaysCs : new Array(p.frames).fill(8);
      if (!this._t0) this._t0 = now;
      let total = 0; const cum = delays.map((d) => (total += d * 10));   // cs → ms
      const t = (now - this._t0) % total;
      let f = 0; while (f < cum.length - 1 && t >= cum[f]) f++;
      this.blit(f);
    },

    // ---- armed attack (overlay canvas) ----
    // Reset the memoized armed pose so the next burst re-resolves it (unit or
    // weapon changed). Safe to call before anything is loaded.
    resetArmed() { this._armedPromise = null; this._armed = null; },
    // Resolve + load this unit's attack pose: the armed swing/poke/bow/gun body
    // with its Mainhand composited, or the plain unarmed `attack` pose when there's
    // no ripped weapon art / it's a monster / bare-handed. Memoized per instance.
    loadArmed() {
      if (!this._armedPromise) this._armedPromise = this._resolveArmed();
      return this._armedPromise;
    },
    async _resolveArmed() {
      const key = this._key, pal = this._pal;
      if (!key) return null;
      const rmod = await renderMod();
      const meta = rmod.SPRITE_BY_KEY[key];
      let item = null, family = 'attack';
      // Only the human rigs (TYPE1/TYPE2) have armed timelines; monsters use `attack`.
      const mh = this.unit && this.unit.Mainhand;
      if (mh && meta && (meta.rig === 'TYPE1' || meta.rig === 'TYPE2')) {
        const lib = await armedMod();
        const wep = await lib.loadWeapons();
        const found = wep.meta.items.find((i) => i.name === mh);
        if (found && meta.anims && meta.anims[found.family]) {
          item = found; family = found.family; this._lib = lib; this._wep = wep;
        }
      }
      const sprite = await loadCached(key, pal, family);
      if (this._dead) return null;
      const poseName = sprite && ((this._poseName && sprite[this._poseName] && this._poseName)
        || (sprite.front ? 'front' : (sprite.back ? 'back' : null)));
      if (!poseName) return null;                        // no attack pose → skip bursts
      let tl = null;
      if (item) {
        tl = this._lib.timelineFor(this._wep.meta, meta.rig, family, item.wt, poseName) || null;
        if (!tl) item = null;                            // no timeline → body-only swing
      }
      const armed = { pose: sprite[poseName], item, tl };
      this._armed = armed;
      return armed;
    },
    sizeAtkCanvas() {
      const cv = this.$refs.atk, A = this._armed; if (!cv || !A) return;
      const p = A.pose;
      cv.width = p.fw + PAD.X * 2; cv.height = p.fh + PAD.T + PAD.B;
      // Display the body at the SAME scale as the walk sprite (size / walkFh) and
      // bottom-anchor it so the feet don't jump; the weapon padding overflows the
      // portrait box upward/sideways (no reflow — the canvas is absolute).
      const scale = this.size / (this._pose ? this._pose.fh : p.fh);
      cv.style.width = (cv.width * scale) + 'px';
      cv.style.height = (cv.height * scale) + 'px';
      cv.style.bottom = (-PAD.B * scale) + 'px';
    },
    drawArmed(f) {
      const cv = this.$refs.atk, A = this._armed; if (!cv || !A) return;
      const p = A.pose;
      const cx = cv.getContext('2d');
      cx.imageSmoothingEnabled = false;
      cx.clearRect(0, 0, cv.width, cv.height);
      const ox = p.ox + PAD.X, oy = p.oy + PAD.T;
      const st = (A.tl && A.item) ? A.tl[Math.min(f, A.tl.length - 1)] : null;
      if (st) this._lib.drawWeapon(cx, this._wep, st, A.item, ox, oy, 1);   // behind body
      cx.drawImage(p.canvas, f * p.fw, 0, p.fw, p.fh, PAD.X, PAD.T, p.fw, p.fh);
      if (st) this._lib.drawWoosh(cx, this._wep, st, ox, oy, 1);            // additive, front
    },

    // ---- burst scheduling ----
    clearBurstTimer() { if (this._burstTimer) { clearTimeout(this._burstTimer); this._burstTimer = 0; } },
    stopBursts() {
      this.clearBurstTimer();
      this.attacking = false;
      this._atk = 0;
    },
    scheduleBurst() {
      this.clearBurstTimer();
      if (this._dead || !this.animated || !this.attackBursts || prefersReducedMotion()) return;
      if (!this.unit || !hasWalkSprite(this.unit.Class, this.unit.Gender)) return;
      const delay = BURST_MIN + Math.random() * (BURST_MAX - BURST_MIN);
      this._burstTimer = setTimeout(() => this.fireBurst(), delay);
    },
    async fireBurst() {
      this._burstTimer = 0;
      if (this._dead || !this.animated || !this.attackBursts) return;
      const A = await this.loadArmed();
      if (this._dead || !this.animated || !this.attackBursts) return;
      if (!A) { this.scheduleBurst(); return; }          // no attack pose — try again later
      this.attacking = true;
      this._atk = 0;
      this.$nextTick(() => { this.sizeAtkCanvas(); this.drawArmed(0); });
      startTicking(this);                                // ensure the ticker is running
    },
    endBurst() {
      this.attacking = false;
      this._atk = 0;
      this.scheduleBurst();                              // queue the next one
    },
  },
};
</script>

<style scoped>
.unit-sprite {
  position: relative;
  display: inline-flex;
  align-items: flex-end;
  justify-content: center;
}
.unit-sprite.flipped { transform: scaleX(-1); }
.us-cv {
  height: 100%; width: auto; display: block;
  image-rendering: pixelated; image-rendering: crisp-edges;
}
/* The armed overlay: absolute so a swing can sweep past the portrait box without
   nudging the grid; bottom/width/height are set per-frame in sizeAtkCanvas. */
.us-atk {
  position: absolute; left: 50%; transform: translateX(-50%);
  pointer-events: none;
  image-rendering: pixelated; image-rendering: crisp-edges;
}
.us-fb { height: 100%; width: auto; object-fit: contain; }
</style>
